// Hook for user-authored recipes — custom-builder and AI-drafted.
//
// Reads every user_recipes row the viewer can SELECT under migration
// 0052's private-by-default RLS — that's the viewer's own rows, plus
// any family recipe with shared=true, plus (for admins) anything in
// the review queue. Exposes:
//
//   recipes        — array of { id, userId, slug, source, recipe,
//                    shared, submittedForReview, reviewStatus,
//                    createdAt, updatedAt } where `recipe` is the
//                    full schema-matching object that CookMode /
//                    findRecipe / suggestMeals can consume.
//   loading        — boolean
//   error          — string | null
//   saveRecipe(recipe, source, opts?) — write a new row. opts is
//                    { shared?, submitForReview? }. submitForReview
//                    also stamps review_status='pending'.
//                    Returns the inserted row.
//   setSharing(id, opts)              — UPDATE an existing row's
//                    { shared?, submitForReview? } — used when the
//                    user schedules a private recipe (scheduling
//                    implies sharing) or from a future library edit
//                    surface.
//   deleteRecipe(id)                  — remove by id (author-only).
//   findBySlug(slug)                  — local lookup for findRecipe().
//   adminList()                       — refresh and return every row
//                    currently in the review queue. Admin-only via
//                    RLS; non-admins get an empty array back.
//   adminDecide(id, { status })       — set review_status +
//                    submitted_for_review=false. Admin-only via RLS.
//
// Realtime: subscribes to user_recipes so a family member's addition
// or an admin's approval shows up without a reload.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";
import { applyVersionSnapshot } from "./recipeEdits";
import { readCache, writeCache } from "./useCachedQuery";

// Infer the right vessel phrase for the goal-image prompt by scanning
// the recipe's tools / ingredients / instructions for cues. Three
// outputs — "skillet", "bowl", or "plate" (default). Heuristic is
// intentionally conservative: when nothing matches, fall back to
// "plate" which is the safest default for any dish.
function inferVesselHint(recipe) {
  if (!recipe || typeof recipe !== "object") return "plate";
  const haystack = [
    Array.isArray(recipe.tools) ? recipe.tools.join(" ") : "",
    Array.isArray(recipe.ingredients) ? recipe.ingredients
      .map(i => (typeof i === "string" ? i : (i?.item || i?.name || ""))).join(" ") : "",
    Array.isArray(recipe.steps) ? recipe.steps
      .map(s => `${s?.title || ""} ${s?.instruction || ""}`).join(" ") : "",
    recipe.title || "",
  ].join(" ").toLowerCase();
  if (/\b(cast[\s-]?iron|skillet|pan[\s-]?fry|pan[\s-]?sear|frittata)\b/.test(haystack)) return "skillet";
  if (/\b(risotto|soup|stew|chili|congee|porridge|ramen|pho|chowder|gumbo|bowl)\b/.test(haystack)) return "bowl";
  return "plate";
}

function fromDb(row) {
  return {
    id:                  row.id,
    userId:              row.user_id,
    slug:                row.slug,
    source:              row.source,
    recipe:              row.recipe || {},
    shared:              row.shared === true,
    submittedForReview:  row.submitted_for_review === true,
    reviewStatus:        row.review_status || null,
    // Community leaderboard opt-in (migration 0236) — separate from
    // family-shared. When true the recipe is world-readable and
    // appears on /community.
    communityPublished:   row.community_published === true,
    communityPublishedAt: row.community_published_at || null,
    createdAt:           row.created_at,
    updatedAt:           row.updated_at,
  };
}

// STALE-WHILE-REVALIDATE cache (added back deliberately, the right way
// this time). The OLD cache was TTL-gated: within 12h it trusted the
// cached list and SKIPPED the network, so a just-saved recipe could
// stay invisible for hours ("I saved a recipe and it doesn't appear").
// This version never trusts the cache as truth — it hydrates from it
// for an instant first paint but ALWAYS refetches on mount and
// overwrites, so the worst staleness is one network round-trip and a
// fresh write can never be hidden. Read with Infinity TTL: the cache
// is a placeholder, age doesn't disqualify it.
const RECIPES_KEY = (uid) => `userRecipes.v3:${uid}`;  // legacy key for one-time cleanup
const SWR_KEY = (uid) => `userRecipes:swr:v1:${uid}`;
const hydrateUR = (uid) => (uid ? readCache(SWR_KEY(uid), Infinity) : null) || [];

export function useUserRecipes(userId) {
  const [recipes, setRecipes] = useState(() => hydrateUR(userId));
  const [loading, setLoading] = useState(() => !!userId && !readCache(SWR_KEY(userId), Infinity));
  const [error,   setError]   = useState(null);

  // Initial load — pulls everything the viewer can see (self +
  // family-shared via RLS). ALWAYS hits the network on mount (cache is
  // just the instant-paint placeholder above); realtime keeps the list
  // in sync for the rest of the session.
  useEffect(() => {
    let alive = true;
    if (!userId) {
      setRecipes([]);
      setLoading(false);
      return;
    }
    // Re-hydrate for THIS user on a switch; spinner only with nothing.
    const cached = readCache(SWR_KEY(userId), Infinity);
    if (cached) setRecipes(cached);
    setLoading(!cached);
    setError(null);
    (async () => {
      const { data, error: err } = await supabase
        .from("user_recipes")
        .select("*")
        .order("created_at", { ascending: false });
      if (!alive) return;
      if (err) {
        console.error("[user_recipes] load failed:", err);
        setError(err.message || "load failed");
        // Keep cached/prior rows on a transient blip instead of blanking.
      } else {
        setRecipes((data || []).map(fromDb));
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId]);

  // Write-through: persist on every successful state (fetch, mutations,
  // realtime all flow through `recipes`). Skip on error so a failed
  // load never overwrites a good cache.
  useEffect(() => {
    if (!userId || error) return;
    if (loading && recipes.length === 0) return;
    writeCache(SWR_KEY(userId), recipes);
  }, [recipes, userId, error, loading]);

  // Realtime — keep the list in sync with inserts/updates/deletes from
  // self OR family. Scoped by userId in the channel name so two tabs
  // don't step on each other.
  const setRecipesRef = useRef(setRecipes);
  useEffect(() => { setRecipesRef.current = setRecipes; }, []);
  useEffect(() => {
    if (!userId) return;
    const ch = safeChannel(`rt:user_recipes:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "user_recipes" }, (payload) => {
        const apply = setRecipesRef.current;
        if (payload.eventType === "INSERT") {
          const row = fromDb(payload.new);
          apply(prev => prev.some(r => r.id === row.id) ? prev : [row, ...prev]);
        } else if (payload.eventType === "UPDATE") {
          // MERGE — don't wholesale-replace. Supabase's UPDATE payloads
          // CAN land with a missing `recipe` column (TOAST-ed jsonb not
          // replicated, or partial-column replication under some
          // configs), and we previously rebuilt state from payload.new
          // alone — which wiped title, ingredients, steps, emoji,
          // everything on any setSharing flip. Keep incoming scalars
          // where they're defined (shared, submitted_for_review, etc.)
          // but preserve the existing `recipe` JSON when the incoming
          // one is falsy or empty. Same defensive principle for slug
          // and source — scalar columns that shouldn't blank under
          // any realistic update but are cheap to guard.
          const incoming = fromDb(payload.new);
          apply(prev => prev.map(r => {
            if (r.id !== incoming.id) return r;
            const mergedRecipe =
              incoming.recipe && Object.keys(incoming.recipe).length > 0
                ? incoming.recipe
                : r.recipe;
            return {
              ...r,
              ...incoming,
              recipe: mergedRecipe,
              slug:   incoming.slug   || r.slug,
              source: incoming.source || r.source,
              userId: incoming.userId || r.userId,
            };
          }));
        } else if (payload.eventType === "DELETE") {
          const id = payload.old?.id;
          if (!id) return;
          apply(prev => prev.filter(r => r.id !== id));
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId]);

  // One-time legacy cache cleanup. Old v1/v2 caches sit in
  // localStorage forever otherwise. Cheap to clear on first mount.
  useEffect(() => {
    if (!userId) return;
    try {
      window.localStorage.removeItem(`userRecipes.v1:${userId}`);
      window.localStorage.removeItem(`userRecipes.v2:${userId}`);
      window.localStorage.removeItem(RECIPES_KEY(userId));
    } catch (_) { /* private mode etc. — ignore */ }
  }, [userId]);

  // slugBase → unique slug for THIS user. Ensures we don't collide with
  // a bundled slug or an earlier custom recipe the same user already
  // saved ("my-stir-fry" → "my-stir-fry-2" on the next save).
  const uniqueSlugFor = useCallback((base) => {
    const cleaned = String(base || "recipe")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "recipe";
    const taken = new Set(recipes.filter(r => r.userId === userId).map(r => r.slug));
    if (!taken.has(cleaned)) return cleaned;
    let n = 2;
    while (taken.has(`${cleaned}-${n}`)) n++;
    return `${cleaned}-${n}`;
  }, [recipes, userId]);

  const saveRecipe = useCallback(async (recipe, source = "custom", opts = {}) => {
    if (!userId)  throw new Error("not signed in");
    if (!recipe)  throw new Error("recipe required");
    const baseSlug = recipe.slug || recipe.title || "recipe";
    // submitForReview is offered for CUSTOM recipes only (the UI gates
    // this) but the hook stays permissive — the DB check constraint
    // enforces the source vocabulary and RLS takes care of visibility.
    const submitForReview = !!opts.submitForReview;

    // Retry on DB-level unique(user_id, slug) violation. The
    // in-memory uniqueSlugFor catches the common case, but a stale
    // realtime subscription or a concurrent save from another tab
    // can still collide — without this guard the second AI-saved
    // recipe with the same title would error out instead of just
    // appending a higher suffix. PG unique-violation is code 23505.
    let slug = uniqueSlugFor(baseSlug);
    let data = null;
    let err = null;
    let attempt = 0;
    const MAX_ATTEMPTS = 5;
    while (attempt < MAX_ATTEMPTS) {
      const stampedRecipe = { ...recipe, slug };
      // Transient cook-routing field (serving-count seed from schedule /
      // book entry points) — never stored recipe content.
      delete stampedRecipe._initialServings;
      // Seed the top-level user_recipes.tags column from the recipe's
      // own tags (cooking-intent + dish-derived) so AI recipes land in
      // the recipe book already filterable. Normalized like setTags
      // (lowercase, trimmed, deduped, capped at 12). Omitted when empty
      // so tagless recipes keep a null column.
      const seedTags = Array.isArray(recipe.tags)
        ? Array.from(new Set(
            recipe.tags
              .filter((t) => typeof t === "string" && t.trim())
              .map((t) => t.toLowerCase().trim()),
          )).slice(0, 12)
        : [];
      const row = {
        user_id: userId,
        slug,
        source,
        recipe: stampedRecipe,
        ...(seedTags.length ? { tags: seedTags } : {}),
        shared: !!opts.shared,
        submitted_for_review: submitForReview,
        review_status: submitForReview ? "pending" : null,
      };
      const result = await supabase
        .from("user_recipes")
        .insert(row)
        .select()
        .single();
      data = result.data;
      err = result.error;
      if (!err) break;
      if (err.code !== "23505") break;
      attempt += 1;
      const baseStem = slug.replace(/-(\d+)$/, "");
      const m = slug.match(/-(\d+)$/);
      const next = (m ? Number(m[1]) : 1) + attempt + 1;
      slug = `${baseStem}-${next}`;
      console.info("[user_recipes] slug collision, retrying with", slug);
    }
    if (err) {
      console.error("[user_recipes] save failed:", err, { slug, source, opts });
      throw new Error(err.message || "save failed");
    }
    const stampedRecipe = { ...recipe, slug };
    // Visible success log so silent-failure cases are diagnosable.
    // eslint-disable-next-line no-console
    console.info("[user_recipes] saved", { id: data?.id, slug, source, shared: !!opts.shared });

    // Optimistic mirror so the new row is visible IMMEDIATELY in
    // THIS hook instance without waiting on realtime. Other mount
    // points hydrate from a fresh network fetch on their own mount;
    // we no longer mirror into localStorage because the cache layer
    // was the source of stale-data bugs. Realtime echo back to a
    // still-mounted instance gets deduped by id in the subscription.
    if (data?.id) {
      const fresh = fromDb(data);
      setRecipes(prev => prev.some(r => r.id === fresh.id) ? prev : [fresh, ...prev]);
    }

    // Goal image: fire-and-forget generation for any new recipe that
    // doesn't already carry a goalImageURL. Hero powers two surfaces
    // that need visual differentiation between recipes — the
    // lock-screen Live Activity (which-cook-is-this when multiple
    // concurrent) and the recipe library tile. Async/parallel so the
    // save returns immediately; the URL patches the user_recipes row
    // server-side once OpenAI lands (gpt-image-1 takes ~10-15s).
    // Realtime UPDATE will broadcast the new URL to this and other
    // mounts. Skipped when:
    //   - recipe already has a goalImageURL (scraped recipes, manual
    //     paste with image)
    //   - save came from a path where the user didn't explicitly
    //     "generate" (per feedback_optional_paid_ai: paid AI is
    //     opt-in). For now we run on every insert — recipe creation
    //     IS the opt-in. Revisit if cost grows.
    // Skip the goal image for a pending background-generation stub
    // (recipe._gen) — it has no real title/ingredients yet, so the
    // image would be generic and wasted. backgroundRecipeGen fires the
    // image itself once the finished recipe lands.
    if (data?.id && !stampedRecipe.goalImageURL && !stampedRecipe._gen) {
      const vessel = inferVesselHint(stampedRecipe);
      supabase.functions.invoke("generate-recipe-image", {
        body: {
          recipeName: stampedRecipe.title || baseSlug,
          vessel,
          recipeId: data.id,
        },
      }).then(({ data: imgData, error: imgErr }) => {
        if (imgErr) {
          console.warn("[user_recipes] goal image gen failed (non-fatal):", imgErr);
          return;
        }
        const url = imgData?.goalImageURL;
        if (!url) return;
        // Patch local mirror so the new image shows up without
        // waiting on the realtime UPDATE round-trip.
        setRecipes(prev => prev.map(r => r.id === data.id
          ? { ...r, recipe: { ...(r.recipe || {}), goalImageURL: url } }
          : r));
      }).catch((e) => {
        console.warn("[user_recipes] goal image invoke threw:", e);
      });
    }

    // XP: +50 for a substantive user-authored recipe. Anti-spam gate:
    // ≥3 steps AND ≥3 ingredients. AI-generated saves ('ai' source)
    // are excluded — the user didn't author those. Fires only on the
    // first save of a given slug (insert already hit conflict on
    // unique(user_id,slug) if re-saved — insert path guarantees
    // first-time here).
    const stepCount = Array.isArray(stampedRecipe.steps) ? stampedRecipe.steps.length : 0;
    const ingCount  = Array.isArray(stampedRecipe.ingredients) ? stampedRecipe.ingredients.length : 0;
    if (source === "custom" && stepCount >= 3 && ingCount >= 3 && data?.id) {
      supabase
        .rpc("award_xp", {
          p_user_id:   userId,
          p_source:    "authored_recipe",
          p_ref_table: "user_recipes",
          p_ref_id:    data.id,
        })
        .then(({ error: xpErr }) => {
          if (xpErr) console.error("[award_xp] authored_recipe failed:", xpErr);
        });
    }

    return fromDb(data);
  }, [userId, uniqueSlugFor]);

  // Toggle sharing / submission flags on an already-saved row. Used by
  // the schedule flow (scheduling implies shared=true) and — later —
  // by a library-edit surface. Scoped to the viewer's own rows via
  // RLS; the optimistic update is safe because a rejected write just
  // gets overwritten by the realtime UPDATE event.
  const setSharing = useCallback(async (id, { shared, submitForReview } = {}) => {
    const patch = {};
    if (typeof shared === "boolean") patch.shared = shared;
    if (typeof submitForReview === "boolean") {
      patch.submitted_for_review = submitForReview;
      if (submitForReview) patch.review_status = "pending";
    }
    if (Object.keys(patch).length === 0) return;
    // Optimistic local update BEFORE the round-trip. Two reasons:
    //   1. The UI pill flips immediately — no waiting for realtime.
    //   2. If realtime ever drops a partial UPDATE payload (see the
    //      merge guard in the subscribe handler), the local state
    //      already carries the right value for this specific flip,
    //      so a buggy realtime replay can't roll us back.
    const prevSnapshot = { shared: undefined, submitted_for_review: undefined, review_status: undefined };
    setRecipes(prev => prev.map(r => {
      if (r.id !== id) return r;
      prevSnapshot.shared = r.shared;
      prevSnapshot.submitted_for_review = r.submittedForReview;
      prevSnapshot.review_status = r.reviewStatus;
      const next = { ...r };
      if (typeof shared === "boolean") next.shared = shared;
      if (typeof submitForReview === "boolean") {
        next.submittedForReview = submitForReview;
        if (submitForReview) next.reviewStatus = "pending";
      }
      return next;
    }));
    const { error: err } = await supabase
      .from("user_recipes")
      .update(patch)
      .eq("id", id);
    if (err) {
      // Roll back the optimistic update so the UI doesn't lie about
      // a share that the server rejected.
      setRecipes(prev => prev.map(r => r.id === id ? {
        ...r,
        shared: prevSnapshot.shared,
        submittedForReview: prevSnapshot.submitted_for_review,
        reviewStatus: prevSnapshot.review_status,
      } : r));
      console.error("[user_recipes] setSharing failed:", err);
      throw new Error(err.message || "update failed");
    }
  }, []);

  const deleteRecipe = useCallback(async (id) => {
    const { error: err } = await supabase
      .from("user_recipes")
      .delete()
      .eq("id", id);
    if (err) {
      console.error("[user_recipes] delete failed:", err);
      throw new Error(err.message || "delete failed");
    }
  }, []);

  // Admin-only queue reader. Non-admins get an empty list back — RLS
  // filters the select, so the client doesn't need to know whether
  // the caller is an admin.
  const adminList = useCallback(async () => {
    const { data, error: err } = await supabase
      .from("user_recipes")
      .select("*")
      .eq("submitted_for_review", true)
      .is("review_status", "pending")
      .order("created_at", { ascending: false });
    if (err) {
      console.error("[user_recipes] adminList failed:", err);
      throw new Error(err.message || "admin list failed");
    }
    return (data || []).map(fromDb);
  }, []);

  // Approve / reject a pending submission. Flipping submitted_for_review
  // to false drops the row out of admin SELECT scope — follow-up edits
  // would need a fresh submission from the author.
  const adminDecide = useCallback(async (id, { status }) => {
    if (!["approved", "rejected"].includes(status)) {
      throw new Error(`invalid review status: ${status}`);
    }
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ review_status: status, submitted_for_review: false })
      .eq("id", id);
    if (err) {
      console.error("[user_recipes] adminDecide failed:", err);
      throw new Error(err.message || "review update failed");
    }
  }, []);

  // Local lookup by slug across every row the viewer can see.
  // findRecipe() in src/data/recipes/index.js checks bundled first;
  // this is the fallback call path for user-authored recipes.
  //
  // Two family members can each author a recipe with the same slug —
  // useUserRecipes loads self + family rows (migration 0052 RLS), so a
  // naive slug→recipe Map collapsed to whichever row landed last and
  // silently swapped one person's recipe for another's at cook time
  // (migration 0139 header has the full writeup). Index by a
  // composite (ownerUserId, slug) key instead, and resolve with an
  // explicit owner hint when the caller knows whose recipe was
  // picked. When the hint is absent, prefer the viewer's own row
  // (the legacy behavior that keeps pre-0139 scheduled meals pointing
  // at self), then fall back to any visible row with that slug.
  const recipesIndex = useMemo(() => {
    const byOwner = new Map();  // `${ownerId}:${slug}` → recipe
    const bySlug  = new Map();  // slug → recipe (first-seen wins; only used when no owner hint)
    const selfBySlug = new Map();
    for (const r of recipes) {
      byOwner.set(`${r.userId}:${r.slug}`, r.recipe);
      if (r.userId === userId) selfBySlug.set(r.slug, r.recipe);
      else if (!bySlug.has(r.slug)) bySlug.set(r.slug, r.recipe);
    }
    return { byOwner, bySlug, selfBySlug };
  }, [recipes, userId]);
  const findBySlug = useCallback((slug, ownerUserId) => {
    if (ownerUserId) {
      return recipesIndex.byOwner.get(`${ownerUserId}:${slug}`) || null;
    }
    return recipesIndex.selfBySlug.get(slug)
        || recipesIndex.bySlug.get(slug)
        || null;
  }, [recipesIndex]);

  // Full recipe-content replacement — App-level in-place save for the
  // post-cook revise flow. Same contract as useRecipeBook's
  // updateRecipeContent (the two hooks watch the same table; realtime
  // UPDATE merge keeps them in sync): optimistic local swap,
  // whole-JSONB write, rollback on error, and the row's SLUG is
  // re-stamped unconditionally — scheduled_meals / cook_logs / shared
  // links join on it.
  const updateRecipeContent = useCallback(async (id, nextRecipe) => {
    if (!id || !nextRecipe || typeof nextRecipe !== "object") return;
    let snapshot = null;
    let stamped = null;
    setRecipes(prev => {
      const target = prev.find((r) => r.id === id);
      if (!target) return prev;
      snapshot = prev;
      // Snapshot prior version so edits aren't destructive (see
      // useRecipeBook). Stored in recipe.versions[].
      stamped = applyVersionSnapshot(target.recipe || {}, { ...nextRecipe, slug: target.slug });
      return prev.map((r) => r.id === id ? { ...r, recipe: stamped } : r);
    });
    if (!stamped) return;
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ recipe: stamped })
      .eq("id", id);
    if (err) {
      console.error("[user_recipes] updateRecipeContent failed:", err);
      if (snapshot) setRecipes(snapshot);
      throw new Error(err.message || "save failed");
    }
  }, []);

  return {
    recipes, loading, error,
    saveRecipe, setSharing, deleteRecipe,
    findBySlug,
    updateRecipeContent,
    adminList, adminDecide,
  };
}
